// src/components/Account/AvailablePlans.jsx
import React, { useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const DEFAULT_PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '0 ₽',
    period: '/6 мес.',
    periodEn: '/6 months',
    desc: 'Базовый ознакомительный доступ к сервису аудита.',
    descEn: 'Basic introductory access to the audit service.',
    badge: null,
    featured: false,
    btnClass: 'plan-btn-active',
    features: [
      { text: 'Basic server information', textRu: 'Базовая информация о сервере', included: true },
      { text: 'Limited tests per day', textRu: 'Лимит тестов в сутки', included: true },
      { text: 'RCON hacking access', textRu: 'Доступ к аудиту RCON', included: false },
      { text: 'Compromised database', textRu: 'База скомпрометированных узлов', included: false },
    ],
  },
  {
    id: 'vip',
    name: 'Vip',
    price: '149 ₽',
    renewPrice: '149 ₽',
    renewAmount: 149,
    period: '/6 мес.',
    periodEn: '/6 months',
    desc: 'Стандартный доступ к RCON и извлечению данных.',
    descEn: 'Standard access to RCON and data extraction.',
    badge: null,
    featured: false,
    btnClass: 'plan-btn-upgrade',
    features: [
      { text: 'Server information', textRu: 'Информация о сервере', included: true },
      { text: 'Data extraction', textRu: 'Извлечение данных', included: true },
      { text: 'RCON hacking (standard access)', textRu: 'RCON аудит (стандартный доступ)', included: true },
      { text: 'Direct RCON Connect', textRu: 'Прямое подключение к RCON', included: true },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '299 ₽',
    renewPrice: '199 ₽',
    renewAmount: 199,
    period: '/6 мес.',
    periodEn: '/6 months',
    desc: 'Глубокий анализ аккаунтов и база скомпрометированных узлов.',
    descEn: 'Deep account analysis and compromised servers database.',
    badge: 'ВЫБОР ЭКСПЕРТОВ',
    badgeEn: 'EXPERT CHOICE',
    featured: true,
    btnClass: 'plan-btn-upgrade',
    features: [
      { text: 'Server information', textRu: 'Информация о сервере', included: true },
      { text: 'Data extraction', textRu: 'Извлечение данных', included: true },
      { text: 'RCON hacking (standard access)', textRu: 'RCON аудит (стандартный доступ)', included: true },
      { text: 'Account checker', textRu: 'Чекер аккаунтов игроков', included: true },
      { text: 'Compromised servers database', textRu: 'База скомпрометированных узлов', included: true },
      { text: 'Server List access', textRu: 'Доступ к базе мониторингов', included: true },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '599 ₽',
    renewPrice: '299 ₽',
    renewAmount: 299,
    period: '/6 мес.',
    periodEn: '/6 months',
    desc: 'Флагманский арсенал: боты, краш-протоколы и эксплойты нулевого дня.',
    descEn: 'Flagship arsenal: bots, crash protocols, and zero-day exploits.',
    badge: 'MAX POWER',
    badgeEn: 'MAX POWER',
    featured: false,
    btnClass: 'plan-btn-upgrade-pro',
    features: [
      { text: 'All Premium features', textRu: 'Все возможности тарифа Premium', included: true },
      { text: 'RCON hacking (full access / speed)', textRu: 'RCON аудит (полный доступ и скорость)', included: true },
      { text: 'OP bot + Crash bot + [...]', textRu: 'OP боты + Краш боты + ботнет', included: true },
      { text: 'Secret hacking methods', textRu: 'Приватные методы аудита', included: true },
      { text: 'DDoS attacks (bots + [...])', textRu: 'Стресс-тесты (DDoS / боты)', included: true },
      { text: 'ZeroDay method', textRu: 'Zero-Day эксплойты', included: true },
    ],
  },
];

const AvailablePlans = ({ plans = [], currentPlan = 'VIP', onSelectPlan, isLoading }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const mergedPlans = useMemo(() => {
    return DEFAULT_PLANS.map((defaultPlan) => {
      const serverPlan = plans.find(
        (p) => p.name?.toLowerCase() === defaultPlan.name.toLowerCase()
      );

      const isCurrent = serverPlan?.isCurrent !== undefined
        ? Boolean(serverPlan.isCurrent)
        : defaultPlan.name.toLowerCase() === (currentPlan || '').toLowerCase();
      const isFree = defaultPlan.name.toLowerCase() === 'free';

      let displayBadge = defaultPlan.badge;
      if (isEn && defaultPlan.badgeEn) displayBadge = defaultPlan.badgeEn;
      if (isCurrent && !isFree) {
        displayBadge = isEn ? 'ACTIVE' : 'АКТИВЕН';
      }

      // Format price & period
      let priceVal = defaultPlan.price;
      let rawPeriod = isEn ? defaultPlan.periodEn : defaultPlan.period;

      if (serverPlan?.price) {
        const rawPrice = String(serverPlan.price);
        priceVal = rawPrice.includes('₽') || rawPrice.includes('$') ? rawPrice : `${rawPrice} ₽`;
      }
      if (serverPlan?.period) {
        rawPeriod = serverPlan.period;
      }

      // Strip any duplicate slashes or whitespace
      let cleanPeriod = String(rawPeriod || '').replace(/^[\/\s]+/, '').trim();
      if (!isEn && /months?/i.test(cleanPeriod)) {
        cleanPeriod = cleanPeriod.replace(/months?/i, 'мес.');
      } else if (isEn && /мес\.?/i.test(cleanPeriod)) {
        cleanPeriod = cleanPeriod.replace(/мес\.?/i, 'months');
      }
      const periodVal = cleanPeriod ? `/${cleanPeriod}` : '';

      // Preserve rich design features with included/excluded flags from defaultPlan
      const features = (defaultPlan.features && defaultPlan.features.length > 0)
        ? defaultPlan.features
        : (serverPlan?.features || []).map((f) =>
            typeof f === 'string' ? { text: f, included: true } : f
          );

      return {
        ...defaultPlan,
        ...(serverPlan || {}),
        features,
        price: priceVal,
        period: periodVal,
        badge: displayBadge,
        isCurrent,
        isFree,
      };
    });
  }, [plans, isEn, currentPlan]);

  return (
    <section className="module-section" id="plansSection">
      {/* Шапка секции тарифов */}
      <div className="module-header">
        <div className="module-title-group">
          <h2 className="module-title">{isEn ? 'Tariff Plans' : 'Тарифные планы'}</h2>
          <p className="module-desc">
            {isEn
              ? 'Transparent access system. All paid tiers are calculated based on a special 6-month billing cycle.'
              : 'Прозрачная система доступа. Все платные планы рассчитываются по специальному 6-месячному расчетному циклу.'}
          </p>
        </div>
      </div>

      {/* Сетка 4 Тарифов */}
      <div className="plans-grid">
        {mergedPlans.map((plan) => {
          const isFree = plan.isFree;
          const isCurrent = plan.isCurrent;

          return (
            <div
              key={plan.id || plan.name}
              className={`plan-card plan-card-${plan.id || plan.name?.toLowerCase()} ${plan.featured ? 'featured' : ''} ${isCurrent ? 'is-current' : ''}`}
            >
              <div className="plan-header">
                {/* Бейдж тарифа */}
                <div className="plan-badge-row">
                  {plan.badge ? (
                    <span className="plan-top-badge">{plan.badge}</span>
                  ) : null}
                </div>

                {/* Название */}
                <h3 className="plan-name">{plan.name}</h3>

                {/* Цена */}
                <div className="plan-price-row">
                  <span className="plan-price-val">{plan.price}</span>
                  <span className="plan-price-period">{plan.period}</span>
                </div>

                {/* Описание */}
                <p className="plan-desc">{isEn ? plan.descEn || plan.desc : plan.desc}</p>
              </div>

              <div className="plan-divider" />

              {/* Список возможностей */}
              <ul className="plan-features-list">
                {plan.features.map((feat, idx) => {
                  const featText =
                    typeof feat === 'string'
                      ? feat
                      : isEn
                      ? feat?.text || ''
                      : feat?.textRu || feat?.text || '';
                  const isIncluded =
                    typeof feat === 'object' && feat !== null ? feat.included !== false : true;

                  return (
                    <li
                      key={idx}
                      className={`plan-feature-item ${!isIncluded ? 'muted' : ''}`}
                    >
                      {isIncluded ? (
                        <svg className="plan-feature-icon" viewBox="0 0 24 24">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg className="plan-feature-icon dimmed" viewBox="0 0 24 24">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      )}
                      <span>{featText}</span>
                    </li>
                  );
                })}
              </ul>

              {/* Кнопка действия */}
              <div className="plan-btn-wrap">
                {isFree ? (
                  <button
                    type="button"
                    className="plan-btn plan-btn-active"
                    disabled
                  >
                    {isEn ? 'Free Tier' : 'Бесплатный'}
                  </button>
                ) : isCurrent ? (
                  <button
                    type="button"
                    className="plan-btn plan-btn-upgrade"
                    onClick={() => !isLoading && onSelectPlan && onSelectPlan(plan, 'extend')}
                    disabled={isLoading}
                  >
                    <span>{isEn ? 'Renew' : 'Продлить'}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`plan-btn ${plan.btnClass || 'plan-btn-upgrade'}`}
                    onClick={() => !isLoading && onSelectPlan && onSelectPlan(plan, 'upgrade')}
                    disabled={isLoading}
                  >
                    <span>{isEn ? 'Upgrade' : 'Выбрать'}</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AvailablePlans;